/**
 * Decode (NOT verify) the payload of a JWT for client-side use.
 *
 * The access token is the source of truth for the user's current claims —
 * auth-service re-reads the DB and re-signs on every /auth/refresh, so a freshly
 * refreshed token already carries the up-to-date role + researchTier. We decode
 * it here purely to mirror those claims into the auth store so guards
 * (ResearchGuard, AdminGuard) reflect them without a separate /profile fetch.
 *
 * Verification is the server's job (it holds the secret); the browser only ever
 * trusts these claims for UI gating, never for authorization — every privileged
 * action is re-checked server-side against the token signature.
 */
export type AccessClaims = {
  role?: string;
  researchTier?: string;
  email?: string;
};

/** base64url → string, browser-safe (handles padding + URL alphabet). */
function decodeBase64Url(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
    const withPad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
    return atob(withPad);
  } catch {
    return null;
  }
}

/** Extract the claims we care about from an access token. Null if unparseable. */
export function decodeAccessClaims(token: string): AccessClaims | null {
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return null;
  const json = decodeBase64Url(parts[1]);
  if (!json) return null;
  try {
    const payload = JSON.parse(json) as Record<string, unknown>;
    const claims: AccessClaims = {};
    if (typeof payload.role === 'string') claims.role = payload.role;
    if (typeof payload.researchTier === 'string') claims.researchTier = payload.researchTier;
    if (typeof payload.email === 'string') claims.email = payload.email;
    return claims;
  } catch {
    return null;
  }
}
